import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Combobox,
  useCombobox,
  Pill,
  PillsInput,
  ScrollArea,
} from "@mantine/core";
import { useCptCodesAdmin } from "../../hooks/admin/useCptCodes";
import { useIcdCodes } from "../../hooks/useReferenceData";
import {
  formatReportCptCodes,
  formatReportIcdCodes,
  mergeReportCptCodes,
  normalizeReportCptCode,
  normalizeReportIcdCode,
  selectReportIcdCodes,
  type ReportCptSource,
} from "../../utils/reportCodes";

const codeFontFamily = "'Times New Roman', Times, serif";

const codeLabelStyle: React.CSSProperties = {
  fontFamily: codeFontFamily,
  fontSize: "9pt",
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: 0,
  color: "#000000",
  whiteSpace: "nowrap",
  flexShrink: 0,
  minWidth: "72.1pt",
};

const codeBodyStyle: React.CSSProperties = {
  fontFamily: codeFontFamily,
  fontSize: "9pt",
  fontWeight: 400,
  lineHeight: 1.15,
  letterSpacing: 0,
  color: "#000000",
};

interface CodeEntry {
  code: string;
  description: string;
  specimenLabel: string;
  source?: ReportCptSource;
}

interface CptIcdCodesSectionProps {
  cptCodes: CodeEntry[];
  icdCodes: CodeEntry[];
  isReadOnly?: boolean;
  onCodesChange?: (type: "cpt" | "icd", displayCodes: string[]) => void;
  liveCptDisplay?: string[] | null;
  liveIcdDisplay?: string[] | null;
  specimenCount?: number;
}

interface CodeOption {
  code: string;
  description: string;
}

function DuplicableCodePicker({
  value,
  options,
  placeholder,
  maxValues,
  onChange,
}: {
  value: string[];
  options: CodeOption[];
  placeholder?: string;
  maxValues?: number;
  onChange: (codes: string[]) => void;
}) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });
  const [search, setSearch] = useState("");

  const handleValueSelect = (code: string) => {
    if (maxValues && value.length >= maxValues) return;
    onChange([...value, code]);
    setSearch("");
  };

  const handleValueRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const filtered = options.filter(
    (opt) =>
      opt.code.toLowerCase().includes(search.toLowerCase().trim()) ||
      opt.description.toLowerCase().includes(search.toLowerCase().trim())
  );

  const pills = value.map((code, index) => (
    <Pill
      key={`${code}-${index}`}
      withRemoveButton
      onRemove={() => handleValueRemove(index)}
      size="xs"
      styles={{ root: { fontSize: 10 } }}
    >
      {code}
    </Pill>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={false}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          size="xs"
          onClick={() => combobox.openDropdown()}
          styles={{ input: { fontSize: 11, minHeight: 28 } }}
        >
          <Pill.Group>
            {pills}
            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder={value.length === 0 ? placeholder : undefined}
                onChange={(event) => {
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                  combobox.openDropdown();
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "Backspace" &&
                    search.length === 0 &&
                    value.length > 0
                  ) {
                    handleValueRemove(value.length - 1);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea.Autosize mah={200} type="scroll">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <Combobox.Option value={opt.code} key={opt.code}>
                  <Text size="xs">
                    {opt.code} — {opt.description}
                  </Text>
                </Combobox.Option>
              ))
            ) : (
              <Combobox.Empty>No results</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

function EditableCodeRow({
  label,
  displayValue,
  currentValues,
  options,
  placeholder,
  isReadOnly,
  onChange,
  maxValues,
  formatValues,
}: {
  label: string;
  displayValue: string;
  currentValues: string[];
  options: CodeOption[];
  placeholder: string;
  isReadOnly?: boolean;
  onChange?: (codes: string[]) => void;
  maxValues?: number;
  formatValues?: (codes: string[]) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [localValues, setLocalValues] = useState(currentValues);
  const [localDisplay, setLocalDisplay] = useState(displayValue);

  useEffect(() => {
    if (!editing) {
      setLocalValues(currentValues);
      setLocalDisplay(displayValue);
    }
  }, [displayValue, currentValues, editing]);

  return (
    <Box
      style={{
        display: "flex",
        gap: 0,
        marginBottom: 0,
        alignItems: "baseline",
      }}
    >
      <span style={codeLabelStyle}>{label}</span>
      {isReadOnly ? (
        <span style={codeBodyStyle}>{localDisplay || "—"}</span>
      ) : editing ? (
        <Box
          style={{ flex: 1 }}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setEditing(false);
            }
          }}
        >
          <DuplicableCodePicker
            value={localValues}
            options={options}
            placeholder={placeholder}
            maxValues={maxValues}
            onChange={(vals) => {
              setLocalValues(vals);
              setLocalDisplay(
                formatValues
                  ? formatValues(vals)
                  : vals.filter(Boolean).join(", ")
              );
              onChange?.(vals);
            }}
          />
        </Box>
      ) : (
        <span
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setEditing(true);
            }
          }}
          style={{
            ...codeBodyStyle,
            cursor: "pointer",
            flex: 1,
            borderRadius: 3,
            padding: "1px 4px",
            marginLeft: -4,
            minHeight: 18,
          }}
          className="cpt-icd-editable-value"
        >
          {localDisplay || (
            <span style={{ color: "#aaa", fontStyle: "italic" }}>
              Click to edit
            </span>
          )}
        </span>
      )}
    </Box>
  );
}

export function CptIcdCodesSection({
  cptCodes,
  icdCodes,
  isReadOnly = false,
  onCodesChange,
  liveCptDisplay,
  liveIcdDisplay,
  specimenCount,
}: CptIcdCodesSectionProps) {
  const { data: allCptCodes = [] } = useCptCodesAdmin(!isReadOnly);
  const { data: allIcdCodes = [] } = useIcdCodes({ enabled: !isReadOnly });

  const mergedCptCodes = mergeReportCptCodes(cptCodes, liveCptDisplay);
  const selectedIcdCodes = selectReportIcdCodes(icdCodes, liveIcdDisplay);
  const formattedCptCodes = formatReportCptCodes(mergedCptCodes);
  const formattedIcdCodes = formatReportIcdCodes(selectedIcdCodes);
  const icdOptions: CodeOption[] = useMemo(
    () =>
      allIcdCodes.map((option) => ({
        code: option.code,
        description: option.label.replace(`${option.code} - `, ""),
      })),
    [allIcdCodes]
  );

  if (
    formattedCptCodes.length === 0 &&
    formattedIcdCodes.length === 0 &&
    isReadOnly
  ) {
    return null;
  }

  const cptOptions: CodeOption[] = allCptCodes
    .filter((c) => c.isActive !== false)
    .map((c) => ({ code: c.code, description: c.description }));

  // Pickers edit specimen diagnosis values only. Stain codes remain derived
  // from completed laboratory work and cannot be accidentally assigned back to
  // specimens when the report-level row is edited.
  const backendDiagnosisCptCodes = cptCodes
    .filter((entry) => !entry.source || entry.source === "diagnosis")
    .map((entry) => entry.code);
  const editableCptCodes = (liveCptDisplay ?? backendDiagnosisCptCodes)
    .map(normalizeReportCptCode)
    .filter((code): code is string => Boolean(code));
  const editableIcdCodes = (
    liveIcdDisplay ?? icdCodes.map((entry) => entry.code)
  )
    .map(normalizeReportIcdCode)
    .filter((code): code is string => Boolean(code));

  const cptInline = formattedCptCodes.join(", ");
  const icdInline = formattedIcdCodes.join(", ");

  return (
    <Box mt={12} style={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
      <EditableCodeRow
        label="CPT CODES:"
        displayValue={cptInline}
        currentValues={editableCptCodes}
        options={cptOptions}
        placeholder="Type CPT code"
        isReadOnly={isReadOnly}
        onChange={(codes) => onCodesChange?.("cpt", codes)}
        maxValues={specimenCount}
        formatValues={(codes) =>
          formatReportCptCodes(mergeReportCptCodes(cptCodes, codes)).join(", ")
        }
      />
      {/* The ICD line is derived-only: one specimen can now carry several ICD
          codes, so a flat report-level list can no longer be mapped back to
          specimens. Codes are edited per specimen in the diagnosis grid or the
          ICD codes widget. */}
      <EditableCodeRow
        label="ICD CODES:"
        displayValue={icdInline}
        currentValues={editableIcdCodes}
        options={icdOptions}
        placeholder="Type ICD code"
        isReadOnly
      />
      <style>{`
        .cpt-icd-editable-value:hover {
          background: rgba(0, 100, 255, 0.04);
          outline: 1px dashed var(--mantine-color-gray-4);
        }
      `}</style>
    </Box>
  );
}
